import { resolvePath, type Scope } from './render.js';
import { isBind, type ComponentDefV2, type SectionInstance } from './document-v2.js';
import type { FieldSpec } from './field.js';

/**
 * Pure resolution: (component contract, section values, document data) →
 * concrete props per rendered page. Runs identically in the editor runtime,
 * the read-only preview, and the PDF pass.
 */

export const FORMATTERS: Record<string, (v: unknown) => string> = {
  currency: (v) =>
    typeof v === 'number'
      ? v.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: v % 1 ? 2 : 0,
        })
      : String(v ?? ''),
  number: (v) => (typeof v === 'number' ? v.toLocaleString('en-US') : String(v ?? '')),
  date: (v) => {
    // A proposal date is a CALENDAR date, not an instant. A date-only string
    // ("2026-12-27") parses as UTC midnight, so formatting it in the viewer's
    // local zone (behind UTC) would render the day before — the off-by-one on
    // the cover. Build the display from the Y/M/D directly, and format any other
    // value in UTC, so the day never shifts across timezones.
    if (typeof v === 'string') {
      const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(v.trim());
      if (ymd) {
        const utc = new Date(Date.UTC(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])));
        return utc.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
      }
    }
    const d = typeof v === 'string' || v instanceof Date ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
      : String(v ?? '');
  },
};

function resolveValue(raw: unknown, scope: Scope): unknown {
  if (isBind(raw)) return resolvePath(scope, raw.$bind);
  if (Array.isArray(raw)) return raw.map((item) => resolveValue(item, scope));
  if (raw !== null && typeof raw === 'object') {
    return Object.fromEntries(
      Object.entries(raw as Record<string, unknown>).map(([k, v]) => [k, resolveValue(v, scope)]),
    );
  }
  return raw;
}

function resolveField(field: FieldSpec, raw: unknown, scope: Scope): unknown {
  const value = raw === undefined ? ('default' in field ? field.default : undefined) : raw;
  const resolved = resolveValue(value, scope);
  if (field.kind === 'binding' && field.format) {
    const fmt = FORMATTERS[field.format];
    return fmt ? fmt(resolved) : resolved;
  }
  return resolved;
}

export interface ResolvedPage {
  /** stable key: sectionId, or sectionId:index inside a repeat */
  key: string;
  sectionId: string;
  componentId: string;
  props: Record<string, unknown>;
}

export function resolveSection(
  component: ComponentDefV2,
  section: SectionInstance,
  data: Record<string, unknown>,
): { pages: ResolvedPage[]; warnings: string[] } {
  const warnings: string[] = [];
  let scopes: { scope: Scope; suffix: string }[];

  if (section.repeat) {
    const items = resolvePath(data, section.repeat.over);
    if (!Array.isArray(items)) {
      warnings.push(`section "${section.id}": repeat.over "${section.repeat.over}" is not an array`);
      scopes = [];
    } else {
      scopes = items.map((item, i) => ({
        scope: { ...data, [section.repeat!.as]: item, '@index': i },
        suffix: `:${i}`,
      }));
    }
  } else {
    scopes = [{ scope: data, suffix: '' }];
  }

  const pages = scopes.map(({ scope, suffix }) => {
    const props: Record<string, unknown> = {};
    for (const field of component.fields) {
      props[field.name] = resolveField(field, section.values[field.name], scope);
    }
    return { key: `${section.id}${suffix}`, sectionId: section.id, componentId: component.id, props };
  });

  return { pages, warnings };
}

export interface ResolvedDocument {
  pages: ResolvedPage[];
  warnings: string[];
}

export function resolveDocument(
  sections: SectionInstance[],
  components: ComponentDefV2[],
  data: Record<string, unknown>,
): ResolvedDocument {
  const byId = new Map(components.map((c) => [c.id, c]));
  const pages: ResolvedPage[] = [];
  const warnings: string[] = [];
  for (const section of sections) {
    const component = byId.get(section.componentId);
    if (!component) {
      warnings.push(`section "${section.id}": unknown component "${section.componentId}"`);
      continue;
    }
    const r = resolveSection(component, section, data);
    pages.push(...r.pages);
    warnings.push(...r.warnings);
  }
  return { pages, warnings };
}
